'use client';

import Cookies from 'js-cookie';
import { useEffect, useState, useRef, useCallback } from 'react';
import { is3rdPartyDisabled } from '../config/config';

declare global {
	interface Window {
		OneTrust: any;
		OnetrustActiveGroups: string;
	}
}

type CookieCategory = 'necessary' | 'performance' | 'functional' | 'ads' | 'social';
type CookieCategories = {
	necessary: boolean;
	performance: boolean;
	functional: boolean;
	ads: boolean;
	social: boolean;
};

const oneTrustElementId = 'onetrust-consent-sdk';

export default function useOneTrust() {
	const [oneTrustLoaded, setOneTrustLoaded] = useState(false);
	const [cookieCategories, setCookieCategories] = useState<Record<CookieCategory, boolean>>({
		necessary: false,
		performance: false,
		functional: false,
		ads: false,
		social: false,
	});
	const isLoading = useRef(false);
	const oneTrustDisabled = is3rdPartyDisabled('one-trust');

	useEffect(() => {
		if (!oneTrustDisabled && !isLoading.current) {
			//start checking for onetrust to be loaded
			checkOneTrustLoaded();
		}
	}, []);

	const attemptsConfig = {
		maxTime: 30000, //at most wait 30 seconds
		retryInterval: 50, //retry every 50 ms
	};
	const checkOneTrustLoaded = (attempts = 0) => {
		if (
			window.OneTrust &&
			window.OnetrustActiveGroups &&
			document.getElementById(oneTrustElementId)
		) {
			onOneTrustLoaded();
		} else {
			isLoading.current = true;
			if (attempts < attemptsConfig.maxTime / attemptsConfig.retryInterval) {
				setTimeout(function () {
					checkOneTrustLoaded(attempts + 1);
				}, attemptsConfig.retryInterval);
			} else {
				//TODO Make sure to handle failure to load
				isLoading.current = false;
				console.error(
					'Failed to load OneTrust after ' + attemptsConfig.maxTime + ' ms. Abandoning wait.'
				);
			}
		}
	};

	const onOneTrustLoaded = () => {
		//set the current cookie categories
		setOneTrustLoaded(true);
		setCookieCategories(getCookieCategories(window.OnetrustActiveGroups?.split(',')));

		//setup the listeners
		if (isLoading.current) {
			const OABCcookieName = 'OptanonAlertBoxClosed';
			const bannerAcceptBtn = document.getElementById('onetrust-accept-btn-handler');
			const pcAllowAllBtn = document.getElementById('accept-recommended-btn-handler');
			const pcSaveBtn = document.getElementsByClassName(
				'save-preference-btn-handler onetrust-close-btn-handler'
			)[0];
			const OABCcookie = Cookies.get(OABCcookieName);

			// IF logic needed here because ot-banner-sdk DIV is not injected on page loads if banner is not exposed
			if (!OABCcookie && bannerAcceptBtn) {
				bannerAcceptBtn.addEventListener('click', function () {
					location.reload();
				});
			}
			if (pcAllowAllBtn)
				pcAllowAllBtn.addEventListener('click', function () {
					location.reload();
				});
			if (pcSaveBtn) {
				pcSaveBtn.addEventListener('click', function () {
					setTimeout(() => {
						location.reload();
					}, 1000); //quick timeout so that the consent receipt can be sent and the cookie can be updated
				});
			}
		}
		isLoading.current = false;
	};

	const getCookieCategories = (groups: string[] = []) => {
		return {
			necessary: groups.includes('C0001'),
			performance: groups.includes('C0002'),
			functional: groups.includes('C0003'),
			ads: groups.includes('C0004'),
			social: groups.includes('C0005'),
		} as CookieCategories;
	};

	const togglePreferences = (e: any) => {
		e.preventDefault();
		window?.OneTrust?.ToggleInfoDisplay?.();
	};

	const utmCaptureParameters = useCallback(async () => {
		const utmCookieName = 'utm_parameters';
		const params = ['cm_cat', 'cm_ven', 'utm_campaign', 'utm_medium', 'utm_source', 'testver'];
		let hasUtmParameters = false;
		let utmJson = {};
		const qs = Object.fromEntries(new URLSearchParams(window.location.search).entries()) || {};
		utmJson = params.reduce(
			(utmAcc, param) => {
				if (qs[param]) {
					utmAcc[param] = qs[param];
				}
				return utmAcc;
			},
			{} as Record<string, any>
		);
		hasUtmParameters = Object.keys(utmJson).length > 0;
		try {
			if (hasUtmParameters) {
				if (cookieCategories.ads) {
					Cookies.set(utmCookieName, JSON.stringify(utmJson), { secure: true });
				} else {
					if (Cookies.get(utmCookieName)) {
						Cookies.set(utmCookieName, '', { secure: true, expires: 0 });
					}
				}
			}
		} catch (e) {
			//console.log('utmCaptureParameters error', e);
		}
	}, [cookieCategories]);

	if (oneTrustDisabled) {
		console.warn('OneTrust is disabled via env config. All cookies will be treated as enabled.');
		return {
			oneTrustLoaded: true,
			cookieCategories: {
				necessary: true,
				performance: true,
				functional: true,
				ads: true,
				social: true,
			},
			togglePreferences: (e: any) => {
				e.preventDefault();
				console.warn('OneTrust is disabled via env config. There is nothing to toggle.');
			},
		};
	}

	return { oneTrustLoaded, cookieCategories, togglePreferences, utmCaptureParameters };
}
